import React, { useMemo } from "react";
import { OrgUnit } from "../../../../api/frameworksApi";
import { Dropdown } from "../../../components/Dropdown";

interface OrgCascadeFilterProps {
  parentOptions: OrgUnit[];
  orgId: string;
  divId: string;
  deptId: string;
  onOrgChange: (id: string) => void;
  onDivChange: (id: string) => void;
  onDeptChange: (id: string) => void;
  disabled?: boolean;
}

/** Only ids that are usable as a select value / React key. */
const hasUsableId = (o: OrgUnit): boolean =>
  !!o && typeof o.id === "string" && o.id.trim().length > 0;

/** Build Dropdown options, deduping ids and falling back to the id as label. */
function toOptions(units: OrgUnit[]): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const o of units) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    out.push({
      value: o.id,
      label: typeof o.name === "string" && o.name.trim() ? o.name : o.id,
    });
  }
  return out;
}

export const OrgCascadeFilter: React.FC<OrgCascadeFilterProps> = ({
  parentOptions,
  orgId,
  divId,
  deptId,
  onOrgChange,
  onDivChange,
  onDeptChange,
  disabled = false,
}) => {
  // A caller mid-fetch (or an unexpected API envelope) can pass undefined/null
  // or entries without ids — never let that reach the filters below.
  const units = useMemo(
    () =>
      (Array.isArray(parentOptions) ? parentOptions : []).filter(hasUsableId),
    [parentOptions],
  );

  // Ids can arrive as null/undefined from URL params or reset handlers.
  const org = typeof orgId === "string" ? orgId : "";
  const div = typeof divId === "string" ? divId : "";
  const dept = typeof deptId === "string" ? deptId : "";

  /* Hierarchy is ORGANIZATION → DEPARTMENT → DIVISION → APPLICATION (the order
   * the /org-units API enforces), so the cascade narrows org → dept → div. */

  const orgOptions = useMemo(
    () => units.filter((o) => o.orgType === "ORGANIZATION"),
    [units],
  );

  const deptOptions = useMemo(
    () =>
      org
        ? units.filter((o) => o.orgType === "DEPARTMENT" && o.parentId === org)
        : [],
    [units, org],
  );

  const divOptions = useMemo(
    () =>
      dept
        ? units.filter((o) => o.orgType === "DIVISION" && o.parentId === dept)
        : [],
    [units, dept],
  );

  /* A selection can outlive its option (org deleted, scope narrowed). Once
   * options have loaded, don't let a Dropdown hold a value it can't label —
   * while still loading, keep the value so a restored filter doesn't flicker. */
  const valueOrBlank = (value: string, opts: OrgUnit[]) =>
    !units.length || opts.some((o) => o.id === value) ? value : "";

  // An org is selected but has no child departments to filter by.
  const orgHasNoDepartments =
    Boolean(org) && units.length > 0 && !deptOptions.length;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Organization filter */}
      <Dropdown
        variant="pill"
        className="min-w-[160px]"
        disabled={disabled}
        value={valueOrBlank(org, orgOptions)}
        onChange={(v) => {
          onOrgChange?.(v);
          onDivChange?.("");
          onDeptChange?.("");
        }}
        options={[
          { value: "", label: "All Organizations" },
          ...toOptions(orgOptions),
        ]}
      />

      {/* Department filter — shown when the selected org has departments */}
      {org && deptOptions.length > 0 && (
        <Dropdown
          variant="pill"
          className="min-w-[160px]"
          disabled={disabled}
          value={valueOrBlank(dept, deptOptions)}
          onChange={(v) => {
            onDeptChange?.(v);
            onDivChange?.("");
          }}
          options={[
            { value: "", label: "All Departments" },
            ...toOptions(deptOptions),
          ]}
        />
      )}

      {/* Replaces the Department filter when the org has no departments. */}
      {orgHasNoDepartments && (
        <span className="text-xs d whitespace-nowrap">
          No departments are available for this organization.
        </span>
      )}

      {/* Division filter — shown when the selected department has divisions */}
      {dept && divOptions.length > 0 && (
        <Dropdown
          variant="pill"
          className="min-w-[160px]"
          disabled={disabled}
          value={valueOrBlank(div, divOptions)}
          onChange={(v) => onDivChange?.(v)}
          options={[
            { value: "", label: "All Divisions" },
            ...toOptions(divOptions),
          ]}
        />
      )}
    </div>
  );
};
